import type { SegmentLabel } from "./schedule-utils";

export type TimeLabelMetrics = {
  barHeight: number;
  labelHeight: number;
  /** Space kept between a label and its line, and between neighbouring labels. */
  gap: number;
};

export type PositionedTimeLabel = SegmentLabel & { top: number };

/**
 * Lay out time labels in pixels so they read top-to-bottom in time order and
 * never overlap.
 *
 * Each label takes its preferred side of its line. If that side runs off the
 * bar or into the previous label, it takes the other side; if both are blocked
 * it sits as close below its line as the previous label and the bar allow.
 */
export function layoutTimeLabels(labels: SegmentLabel[], metrics: TimeLabelMetrics): PositionedTimeLabel[] {
  const { barHeight, labelHeight, gap } = metrics;
  const sorted = [...labels].sort((a, b) => a.percent - b.percent);

  const positioned: PositionedTimeLabel[] = [];
  let minTop = 0;
  for (const label of sorted) {
    const lineY = (label.percent / 100) * barHeight;
    const aboveTop = lineY - gap - labelHeight;
    const belowTop = lineY + gap;
    const fits = (top: number) => top >= minTop && top + labelHeight <= barHeight;

    const [preferred, other] = label.position === "above" ? [aboveTop, belowTop] : [belowTop, aboveTop];
    let top: number;
    if (fits(preferred)) top = preferred;
    else if (fits(other)) top = other;
    else top = Math.max(minTop, Math.min(belowTop, barHeight - labelHeight));

    positioned.push({ ...label, top });
    minTop = top + labelHeight + gap;
  }

  return positioned;
}
